/**
 * @license Copyright (c) 2003-2020, CKSource - Frederico Knabben. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

/**
 * @module link/linkui
 */

import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ClickObserver from '@ckeditor/ckeditor5-engine/src/view/observer/clickobserver';
import { isLinkElement } from '@ckeditor/ckeditor5-link/src/utils';

const linkKeystroke = 'Ctrl+K';

export default class RdsLinkUI extends Plugin {

	static get requires() {
		return [ ];
	}

	static get pluginName() {
		return 'RdsLinkUI';
	}


    _toolbarElement;
    set toolbarElement(element) {
        this._toolbarElement = element;
        if (element) {
            this.clickOutsideHandler( {
                emitter: this.editor.ui.view,
                activator: () => !!this.toolbarElement,
                contextElements: [ this.toolbarElement ],
                callback: () => {
                    this._hideUI()
                }
            } );
            const attachToElementRect = this._getToolbarAttachToElement().getBoundingClientRect();
            this.scrollHandler( {
                emitter: this.editor.ui.view,
                bottom: attachToElementRect.bottom,
                callback: (newBottom) => {
                    this.fire('updateToolbarPosition', newBottom);
                }
            } );
        } else {
    		this.editor.ui.view.stopListening( document, 'mousedown' );
    		this.editor.ui.view.stopListening( document, 'scroll' );
        }
    }

    get toolbarElement() {
        return this._toolbarElement
    }

    scrollHandler( { emitter, callback, bottom  } ) {
        let currentBottom = bottom;
        emitter.listenTo( document, 'scroll', ( evt, { target } ) => {
            const updatedAttachToElementRect = this._getToolbarAttachToElement().getBoundingClientRect();
            let newBottom = updatedAttachToElementRect.bottom + window.scrollY;
            if (newBottom === currentBottom) {
                return
            }
            currentBottom = newBottom
            callback(newBottom);
        } );
    }

    clickOutsideHandler( { emitter, activator, callback, contextElements } ) {
        emitter.listenTo( document, 'mousedown', ( evt, { target } ) => {
            if ( !activator() ) {
                return;
            }
    
            for ( const contextElement of contextElements ) {
                if ( contextElement.contains( target ) ) {
                    return;
                }
            }
            callback();
        } );
    }

	init() {
		const editor = this.editor;

		editor.editing.view.addObserver( ClickObserver );

		// Handle the `Ctrl+K` keystroke and show the panel.
		editor.keystrokes.set( linkKeystroke, ( keyEvtData, cancel ) => {
			// Prevent focusing the search bar in FF, Chrome and Edge. See https://github.com/ckeditor/ckeditor5/issues/4811.
			cancel();

			this._showUI( true );
		} );
        
        this._enableUserInteractions()
	}

	/**
	 * @inheritDoc
	 */
	destroy() {
		super.destroy();
	}

	_enableUserInteractions() {
		const viewDocument = this.editor.editing.view.document;

		// Handle click on view document and show panel when selection is placed inside the link element.
		// Keep panel open until selection will be inside the same link element.
		this.listenTo( viewDocument, 'click', () => {
			const parentLink = this._getSelectedLinkElement();
			if ( parentLink ) {
				// Then show panel but keep focus inside editor editable.
				this._showUI();
			}
		} );

		// Close the panel on the Esc key press when the editable has focus and the balloon is visible.
		this.editor.keystrokes.set( 'Esc', ( data, cancel ) => {
				this._hideUI();
				cancel();
		} );
	}

	_showUI( ) {
        let config = {
            element: this._getToolbarAttachToElement()
        };
        const linkElement = this._getSelectedLinkElement();
		// When there's no link under the selection, go straight to the editing UI.
		if ( !linkElement ) {
			config = {
                ...config,
                mode: 'new',
                href: ''
            }
		}
		// If there's a link under the selection...
		else {
			config = {
                ...config,
                mode: 'existing',
                href: linkElement._attrs.get('href')

            }
		}

        this.fire('showLinkUI', config);
	}

	_hideUI() {
		const editor = this.editor;

		this.stopListening( editor.ui, 'update' );
		this.stopListening( this._balloon, 'change:visibleView' );

		// Make sure the focus always gets back to the editable _before_ removing the focused form view.
		// Doing otherwise causes issues in some browsers. See https://github.com/ckeditor/ckeditor5-link/issues/193.
		editor.editing.view.focus();
        this.fire('hideLinkUI');

	}

	_getToolbarAttachToElement() {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		const targetLink = this._getSelectedLinkElement();

		const target = targetLink ?
			// When selection is inside link element, then attach panel to this element.
			view.domConverter.mapViewToDom( targetLink ) :
			// Otherwise attach panel to the selection.
			view.domConverter.viewRangeToDom( viewDocument.selection.getFirstRange() );

		return target;
	}

	_getSelectedLinkElement() {
		const view = this.editor.editing.view;
		const selection = view.document.selection;

		if ( selection.isCollapsed ) {
			return findLinkElementAncestor( selection.getFirstPosition() );
		} else {
			// The range for fully selected link is usually anchored in adjacent text nodes.
			// Trim it to get closer to the actual link element.
			const range = selection.getFirstRange().getTrimmed();
			const startLink = findLinkElementAncestor( range.start );
			const endLink = findLinkElementAncestor( range.end );

			if ( !startLink || startLink != endLink ) {
				return null;
			}

			// Check if the link element is fully selected.
			if ( view.createRangeIn( startLink ).getTrimmed().isEqual( range ) ) {
				return startLink;
			} else {
				return null;
			}
		}
	}
}

function findLinkElementAncestor( position ) {
	return position.getAncestors().find( ancestor => isLinkElement( ancestor ) );
}